import type { ClassicCardSide } from '../types'
import { isBlank } from './fieldValue'

/**
 * Legacy capitalised taxonomy (kept for backward compat / backend mirror).
 * The panel now uses the /kinds/ endpoint instead of this static set.
 */
export const CARD_SIDE_KINDS: readonly string[] = [
  'Definition',
  'Example',
  'Translation',
  'Image',
  'Link',
  'Audio',
  'Dialog',
]

/** Canonical Side-B kinds (lowercase, ADR-040). */
const CANONICAL_KIND_SET = new Set([
  'definition', 'example', 'translation', 'image', 'link', 'audio', 'dialog',
])

/**
 * Human-readable display name for each canonical kind.
 * Shown in the panel rows and the mini-preview label.
 * "note" is the catch-all for custom-labelled sides — the panel shows "Note"
 * as a generic slot name (the cosmetic label is not surfaced at this layer).
 */
export const KIND_DISPLAY_NAMES: Record<string, string> = {
  definition:  'Definition',
  example:     'Example',
  translation: 'Translation',
  image:       'Image',
  link:        'Link',
  audio:       'Audio',
  dialog:      'Dialog',
  note:        'Note',
}

/**
 * The arrangement a reader who has never opened the panel gets (ADR-092).
 *
 * Until Classic could resolve a curated translation there were two parts to
 * arrange and the default named neither: it took whatever came first, which on
 * every deck is the definition. Now that a system card carries a translation
 * too, "whatever comes first" would keep the translation off the face of every
 * reader who does not go looking for the setting — which is the complaint the
 * resolution fix exists to answer, left half-answered.
 *
 * Definition and translation are the two parts that say what the word means;
 * the example is the one the collapsed face can least afford (it is the longest,
 * and pre-flip it is withheld entirely when it cannot be blanked — ADR-068
 * Decision 7). So they are the two slots, in the panel's own canonical order.
 *
 * This is a default, not a migration. It applies only when `collapsed_parts` is
 * null — the stored value meaning "this reader has expressed no arrangement".
 * A reader who has saved one keeps exactly the parts and slots they picked: a
 * non-null config never reaches this list. Nothing is written to the database
 * by rendering, so no stored preference changes, here or on the server.
 *
 * A card that has only one of the two renders that one, exactly as it does
 * today; a card that has neither falls through to the first-available guard
 * below. Neither case ever produces an empty slot or a placeholder.
 */
const DEFAULT_COLLAPSED_KINDS: readonly string[] = ['definition', 'translation']

/**
 * Given the full ordered Side-B part list and the user's configured kind list,
 * returns the 1–2 parts to display in the collapsed card view (ADR-039/040).
 *
 * Rules:
 *  - Only filled parts are candidates (ADR-104 Decisions 4 and 7): a blank part
 *    is never a slot, so each configured kind resolves to the card's VALUE side
 *    for it — the first non-blank part of the kind — and a kind whose parts are
 *    all blank counts as absent.
 *  - null / empty config → DEFAULT_COLLAPSED_KINDS, then first-available
 *  - Config entries are kind values (e.g. "definition"), matched against the
 *    part's `kind` (ADR-040). A part without a `kind` (an older payload) falls
 *    back to its case-folded label. Legacy label-exact matching is tried second
 *    for any still-capitalised stored values (e.g. old "Definition").
 *  - "note" is a catch-all: matches the first note-kind part (or, without a
 *    kind, the first part whose label does not fold to a canonical kind name).
 *  - Configured parts absent on this card are skipped.
 *  - All configured parts absent → first filled part.
 *  - No filled part at all → [] — the caller states that the face is empty
 *    rather than rendering a blank card (ADR-104 Decision 7).
 *
 * Two-part collapsed clip behaviour (shipped): lines-each — 3 lines per slot
 * in two-part mode, independent line-clamp per slot with a bottom fade. Each
 * slot clips independently rather than sharing a combined height budget. This
 * surfaces the beginning of each part equally regardless of which is longer.
 */
export function resolveCollapsedParts(
  allParts: ClassicCardSide[],
  configuredKeys: string[] | null,
): ClassicCardSide[] {
  const keys = (!configuredKeys || configuredKeys.length === 0)
    ? DEFAULT_COLLAPSED_KINDS
    : configuredKeys

  const filled = allParts.filter((p) => !isBlank(p.content))
  const kindOf = (p: ClassicCardSide) => p.kind ?? p.label.trim().toLowerCase()

  const resolved = keys.flatMap(k => {
    // 1. Kind-based: the first filled part of this kind is the card's value.
    const byKind = filled.find(p => kindOf(p) === k)
    if (byKind) return [byKind]

    // 2. Legacy exact-label match (old stored "Definition" → label "Definition").
    const byLabel = filled.find(p => p.label === k)
    if (byLabel) return [byLabel]

    // 3. "note" catch-all: first custom part.
    if (k === 'note') {
      const noteSide = filled.find(
        p => (p.kind ? p.kind === 'note' : !CANONICAL_KIND_SET.has(kindOf(p))),
      )
      if (noteSide) return [noteSide]
    }

    return []
  })

  return resolved.length > 0 ? resolved : filled.slice(0, 1)
}
